import { BillingAccessStateEnum } from '@/gen/cortado/v1/subscriptions_pb'

export type CheckoutPollOutcome = 'idle' | 'polling' | 'succeeded' | 'stuck'
export type CheckoutPollMode = 'embedded' | 'redirect'
export type CheckoutPollTerminalOutcome = 'succeeded' | 'stuck' | 'canceled'

const DEFAULT_INTERVAL_MS = 2000
const DEFAULT_ATTEMPTS_PER_PHASE = 15

export function isLiveCheckoutState(state: BillingAccessStateEnum): boolean {
  switch (state) {
    case BillingAccessStateEnum.BILLING_ACCESS_STATE_TRIALING:
    case BillingAccessStateEnum.BILLING_ACCESS_STATE_ACTIVE:
    case BillingAccessStateEnum.BILLING_ACCESS_STATE_GRACE:
    case BillingAccessStateEnum.BILLING_ACCESS_STATE_PAUSED_PAYMENT:
    case BillingAccessStateEnum.BILLING_ACCESS_STATE_COMP:
      return true
    default:
      return false
  }
}

export interface BillingCheckoutPollOptions {
  mode: CheckoutPollMode
  beforeState: BillingAccessStateEnum | undefined
  signal: AbortSignal
  refetchState: () => Promise<BillingAccessStateEnum | undefined>
  reconcile: () => Promise<void>
  invalidate: () => void
  // Runs reconcile once before phase 0's first polling attempt instead of
  // only at the phase 0->1 boundary. For an explicit user-initiated retry,
  // waiting another 30s of pure polling before the first reconcile attempt
  // would just repeat the stall the user is trying to escape.
  reconcileImmediately?: boolean
  // A reconcile failure (a persistent backend error, not just a pending
  // provider state) is otherwise invisible - both fall through to phase two
  // identically. Report it so the two are distinguishable in telemetry.
  onReconcileError?: (error: unknown) => void
  intervalMs?: number
  attemptsPerPhase?: number
  wait?: (delayMs: number, signal: AbortSignal) => Promise<boolean>
}

export interface BillingCheckoutPollResult {
  outcome: CheckoutPollTerminalOutcome
  baseline: BillingAccessStateEnum | undefined
}

async function waitForAttempt(delayMs: number, signal: AbortSignal): Promise<boolean> {
  if (signal.aborted) return false
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve(true)
    }, delayMs)
    const onAbort = () => {
      clearTimeout(timeout)
      resolve(false)
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

async function reconcileOnce(options: BillingCheckoutPollOptions): Promise<void> {
  try {
    await options.reconcile()
    options.invalidate()
  } catch (error) {
    // Pending provider state and transient failures both enter phase two -
    // report so a persistent backend failure is no longer indistinguishable
    // from state that's genuinely still pending.
    options.onReconcileError?.(error)
  }
}

// pollBillingCheckout is the async state machine behind the React hook. Its
// provider reads, reconciliation, clock, and cancellation are all injected so
// phase boundaries and late-result behavior can be tested without React.
export async function pollBillingCheckout(
  options: BillingCheckoutPollOptions
): Promise<BillingCheckoutPollResult> {
  let baseline = options.beforeState
  if (options.mode === 'redirect' && baseline !== undefined && isLiveCheckoutState(baseline)) {
    return { outcome: 'succeeded', baseline }
  }

  const wait = options.wait ?? waitForAttempt
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS
  const attemptsPerPhase = options.attemptsPerPhase ?? DEFAULT_ATTEMPTS_PER_PHASE

  if (options.reconcileImmediately) {
    await reconcileOnce(options)
    if (options.signal.aborted) return { outcome: 'canceled', baseline }
  }

  for (let phase = 0; phase < 2; phase++) {
    for (let attempt = 0; attempt < attemptsPerPhase; attempt++) {
      if (!(await wait(intervalMs, options.signal)) || options.signal.aborted) {
        return { outcome: 'canceled', baseline }
      }

      let newState: BillingAccessStateEnum | undefined
      try {
        newState = await options.refetchState()
      } catch {
        // A failed read consumes one bounded attempt; the query owns the error.
      }
      if (options.signal.aborted) return { outcome: 'canceled', baseline }

      if (newState !== undefined) {
        if (baseline === undefined) {
          baseline = newState
          if (isLiveCheckoutState(newState)) return { outcome: 'succeeded', baseline }
        } else if (
          isLiveCheckoutState(newState) &&
          (options.mode === 'redirect' || newState !== baseline)
        ) {
          baseline = newState
          return { outcome: 'succeeded', baseline }
        } else if (!isLiveCheckoutState(newState)) {
          baseline = newState
        }
      }
    }

    if (phase === 0) {
      await reconcileOnce(options)
      if (options.signal.aborted) return { outcome: 'canceled', baseline }
    }
  }

  return { outcome: 'stuck', baseline }
}
